import React, {
  useEffect,
  useCallback,
  useRef,
  useMemo,
  useState,
} from 'react';
import _get from 'lodash/get';
import { useSelector } from 'react-redux';
import { isFirefoxBrowser } from '../../utils/utils';

export default function VisibilityProctor({
  test,
  testSolutionSet,
  visibilityEnabled,
  enableVisibility,
  disableVisibility,
  socketActive,
  trackWindowBlur,
  trackWindowFocus,
  isBrowserToleranceEnabled,
  showBrowserTolerance,
}) {
  // Changes in logic has been done for iframe
  // in future when we are integrating more iframe
  // like jupyter, then we can have common reducer variable
  // can use that in place of isPodBasedProblem

  const [isListnerAdded, setIsListnerAdded] = useState(false);

  const browserToleranceCount = useSelector(
    state => state.proctor.browserToleranceCount
  );

  const toleranceCountRef = useRef();
  const isPodBasedProblemRef = useRef();
  const isAutoSubmitInProcessRef = useRef();

  isAutoSubmitInProcessRef.current = test.isTestSubmitted;

  toleranceCountRef.current = useMemo(() => {
    if (typeof browserToleranceCount === 'number') return browserToleranceCount;
    return undefined;
  }, [browserToleranceCount]);

  isPodBasedProblemRef.current = useSelector(
    state => state.assessment?.isPodBased
  );

  const trackFocus = useCallback(() => {
    if (isAutoSubmitInProcessRef.current) {
      window.removeEventListener('focus', trackFocus, false);
      return;
    }
    const shouldFocusTrigger = isPodBasedProblemRef.current
      ? !window.isLastEventTriggeredFromIframe && window.lastEvent === 'blur'
      : true;
    if (
      shouldFocusTrigger &&
      (!isBrowserToleranceEnabled ||
        (isBrowserToleranceEnabled && toleranceCountRef.current >= -1))
    ) {
      trackWindowFocus();
      window.lastEvent = 'focus';
      if (isBrowserToleranceEnabled && toleranceCountRef.current === -1)
        window.removeEventListener('focus', trackFocus, false);
    }
    window.isLastEventTriggeredFromParent = true;
    window.isLastEventTriggeredFromIframe = false;
  }, [
    isBrowserToleranceEnabled,
    toleranceCountRef.current,
    isPodBasedProblemRef.current,
    isAutoSubmitInProcessRef.current,
  ]);

  const trackBlur = useCallback(() => {
    if (
      !isBrowserToleranceEnabled ||
      (isBrowserToleranceEnabled && toleranceCountRef.current >= 0)
    ) {
      if (isAutoSubmitInProcessRef.current) {
        window.removeEventListener('blur', trackBlur, false);
        return;
      }
      if (isFirefoxBrowser() && isPodBasedProblemRef.current) {
        setTimeout(() => {
          if (!document.hasFocus() && window.lastEvent !== 'blur') {
            trackWindowBlur();
            window.lastEvent = 'blur';
            if (isBrowserToleranceEnabled && toleranceCountRef.current === 0)
              window.removeEventListener('blur', trackBlur, false);
          }
          window.isLastEventTriggeredFromParent = true;
          window.isLastEventTriggeredFromIframe = false;
        }, 0);
      } else {
        const shouldBlurTrigger = isPodBasedProblemRef.current
          ? !document.hasFocus() && window.lastEvent !== 'blur'
          : true;
        if (shouldBlurTrigger) {
          trackWindowBlur();
          window.lastEvent = 'blur';
          if (isBrowserToleranceEnabled && toleranceCountRef.current === 0)
            window.removeEventListener('blur', trackBlur, false);
        }
        window.isLastEventTriggeredFromParent = true;
        window.isLastEventTriggeredFromIframe = false;
      }
    }
  }, [
    isBrowserToleranceEnabled,
    toleranceCountRef.current,
    isPodBasedProblemRef.current,
    isAutoSubmitInProcessRef.current,
  ]);

  const { is_expired } = test;
  let proctorEnabled = _get(test, 'settings.proctor.enabled');
  proctorEnabled = proctorEnabled && !!_get(testSolutionSet, 'id');

  useEffect(() => {
    if (
      proctorEnabled &&
      !is_expired &&
      visibilityEnabled &&
      !isListnerAdded &&
      socketActive
    ) {
      if (showBrowserTolerance && toleranceCountRef.current === -1) {
        return;
      }
      window.addEventListener('focus', trackFocus, false);
      window.addEventListener('blur', trackBlur, false);
      setIsListnerAdded(true);
    }
  }, [
    proctorEnabled,
    is_expired,
    visibilityEnabled,
    isListnerAdded,
    socketActive,
    toleranceCountRef.current,
    showBrowserTolerance,
  ]);

  const disableVisibilityTracking = useCallback(() => {
    window.removeEventListener('focus', trackFocus, false);
    window.removeEventListener('blur', trackBlur, false);
  }, [trackFocus, trackBlur]);

  useEffect(() => {
    if (!is_expired && proctorEnabled && !visibilityEnabled) enableVisibility();
  }, [visibilityEnabled, proctorEnabled, is_expired, enableVisibility]);

  // Removing visibility tracking event listiners after test has ended
  useEffect(() => {
    if (is_expired && proctorEnabled) {
      disableVisibilityTracking();
      disableVisibility();
    }
  }, [
    proctorEnabled,
    is_expired,
    disableVisibility,
    disableVisibilityTracking,
  ]);

  return <></>;
}
